import { Formik, Form } from "formik";
import { RxCross2 } from "react-icons/rx";
import { FaFileVideo, FaFileImage } from "react-icons/fa";
import React, { useEffect, useRef, useState } from "react";
import { MdAdd, MdCloudUpload, MdOutlinePlayArrow } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { CSpinner } from "@coreui/react-pro";
import ModalV2 from "../../components/customModal/ModalV2";
import CustomPagination from "../../components/customPagination";
import {
  ACCEPTED_TYPES,
  AllowedVideoTypes,
  MAX_FILE_SIZE_MB,
  AllowedImageTypes,
  MAX_VIDEO_SIZE_BYTES,
  MAX_VIDEO_SIZE_MB,
  MAX_IMAGE_SIZE_BYTES,
  MAX_IMAGE_SIZE_MB,
  HOW_TO_VIDEO_STATUS,
  HOW_TO_VIDEO_STATUS_OPTIONS,
  HOW_TO_USE_STATUS,
  HOW_TO_USE_STATUS_LABELS,
} from "../../utils/contentTypes";
import StatusSelect from "../../components/statusSelect";
import { dateFormatter, formatFileSize } from "../../utils/commonFunctions";
import {
  addHowToVideosAddValidationSchema,
  editHowToVideosValidationSchema,
} from "../../utils/validationSchema";
import { UPDATE_HOW_TO_VIDEOS_LIST } from "../../store/actionTypes";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBin2Line, RiDeleteBinLine } from "react-icons/ri";
import {
  getHowToVideosList,
  deleteHowToVideo,
  HowToVideosService,
} from "./action";
import "./style.css";
import toast from "react-hot-toast";

const HOW_TO_VIDEOS_PAGE_SIZE_OPTIONS = [3, 6, 12, 24, 60];

const HowToVideos = () => {
  const dispatch = useDispatch();
  const { howToVideosList, pageNo, limit, totalPage, isLoading } = useSelector(
    (state) => state.HowToVideosReducer,
  );

  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingThumbnail, setIsDraggingThumbnail] = useState(false);
  const [playingCardId, setPlayingCardId] = useState(null);

  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const activeGridVideoRef = useRef(null);

  const isEditMode = Boolean(selectedVideo);

  const handleGridVideoPlay = (videoEl) => {
    if (activeGridVideoRef.current && activeGridVideoRef.current !== videoEl) {
      activeGridVideoRef.current.pause();
    }
    activeGridVideoRef.current = videoEl;
  };

  const handleGridVideoPause = (videoEl) => {
    if (activeGridVideoRef.current === videoEl) {
      activeGridVideoRef.current = null;
    }
  };
  const handleThumbnailClick = (id) => {
    setPlayingCardId(id);
  };

  useEffect(() => {
    dispatch(getHowToVideosList());
  }, [pageNo, limit]);

  const updatePage = (page) => {
    dispatch({
      type: UPDATE_HOW_TO_VIDEOS_LIST,
      payload: { pageNo: page },
    });
  };

  const pageLimitHandler = (e) => {
    const currLimit = e?.target?.value;
    dispatch({
      type: UPDATE_HOW_TO_VIDEOS_LIST,
      payload: { limit: currLimit, pageNo: 1 },
    });
  };

  const handleAdd = () => {
    setSelectedVideo(null);
    setVideoPreviewUrl(null);
    setThumbnailPreviewUrl(null);
    setAddModal(true);
  };

  const handleEdit = (data) => {
    console.log("data on edit :", data);
    setSelectedVideo(data);
    setVideoPreviewUrl(data.videoUrl);
    setThumbnailPreviewUrl(data.thumbnail || null);
    setAddModal(true);
  };

  const closeModal = (resetForm) => {
    setAddModal(false);

    if (videoPreviewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    if (thumbnailPreviewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }

    setVideoPreviewUrl(null);
    setThumbnailPreviewUrl(null);
    setSelectedVideo(null);
    setIsDragging(false);
    setIsDraggingThumbnail(false);

    resetForm?.();
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (isEditMode) {
      if (
        values.title.trim() === selectedVideo?.title?.trim() &&
        !values.video &&
        values.existingVideoUrl === selectedVideo?.videoUrl &&
        !values.thumbnail &&
        values.existingThumbnailUrl === (selectedVideo?.thumbnail || "") &&
        values.status === selectedVideo?.status
      ) {
        toast.error(
          "Nothing has changed yet. Edit the title or upload a new video/thumbnail to continue.",
        );
        setSubmitting(false);
        closeModal(resetForm);
        return;
      }

      const response = await HowToVideosService.updateHowToVideo({
        id: selectedVideo._id,
        title: values.title.trim(),
        video: values.video ? values.video : values.existingVideoUrl,
        thumbnail: values.thumbnail
          ? values.thumbnail
          : values.existingThumbnailUrl,
        status: values.status,
        previousVideoUrl: selectedVideo?.videoUrl,
        previousThumbnailUrl: selectedVideo?.thumbnail,
      });
      setSubmitting(false);
      if (response) {
        closeModal(resetForm);
        dispatch(getHowToVideosList());
      }
      return;
    }

    const response = await HowToVideosService.createHowToUsePost({
      title: values.title.trim(),
      video: values.video,
      thumbnail: values.thumbnail,
      status: values.status,
    });
    setSubmitting(false);
    if (response) {
      closeModal(resetForm);
      dispatch(getHowToVideosList());
    }
  };

  const handleDelete = (data) => {
    console.log("Delete:", data);
    setDeleteTarget(data);
    setDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleteTarget?._id) {
      dispatch(deleteHowToVideo(deleteTarget._id));
    }
    setDeleteModal(false);
    setDeleteTarget(null);
  };

  const renderVideoGrid = () => {
    if (isLoading) {
      return (
        <div className="how-to-videos-grid how-to-videos-empty-state">
          <div className="text-center py-5 w-100">
            <CSpinner color="primary" />
            <div className="mt-2">Fetching how to videos...</div>
          </div>
        </div>
      );
    }

    if (!howToVideosList?.length) {
      return (
        <div className="how-to-videos-grid how-to-videos-empty-state">
          <div className="text-center py-5 w-100 text-muted">
            No how to videos available.
          </div>
        </div>
      );
    }

    return (
      <div className="how-to-videos-grid">
        {howToVideosList.map((data) => {
          const isPlaying = playingCardId === data._id;

          return (
            <div key={data._id} className="video-card">
              <div className="video-card-thumb">
                {isPlaying ? (
                  <video
                    key={data.videoUrl}
                    className="video-card-thumb-media"
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                    src={data.videoUrl}
                    onPlay={(e) => handleGridVideoPlay(e.currentTarget)}
                    onPause={(e) => handleGridVideoPause(e.currentTarget)}
                  />
                ) : (
                  <div
                    className="video-card-thumbnail-wrapper"
                    onClick={() => handleThumbnailClick(data._id)}
                  >
                    {data.thumbnail ? (
                      <img
                        src={data.thumbnail}
                        alt={data.title}
                        className="video-card-thumbnail-image"
                      />
                    ) : (
                      <video
                        className="video-card-thumbnail-image"
                        src={data.videoUrl}
                        muted
                        playsInline
                        preload="metadata"
                      />
                    )}

                    <div className="video-card-play-overlay">
                      <span className="video-card-play-btn">
                        <MdOutlinePlayArrow />
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="video-card-body">
                <h4 className="video-card-title" title={data.title}>
                  {data.title}
                </h4>

                {data.createdAt && (
                  <div className="video-card-date">
                    <span>Added on {dateFormatter(data.createdAt)}</span>

                    <span className={`video-card-status ${data.status === HOW_TO_USE_STATUS.DRAFT ? "draft" : "published"}`}>{HOW_TO_USE_STATUS_LABELS[data.status]}</span>
                  </div>
                )}
              </div>

              <div className="video-card-footer">
                <button
                  type="button"
                  className="video-card-action video-card-action-edit"
                  onClick={() => handleEdit(data)}
                >
                  <FiEdit2 />
                  <span>Edit</span>
                </button>

                <button
                  type="button"
                  className="video-card-action video-card-action-delete"
                  onClick={() => handleDelete(data)}
                >
                  <RiDeleteBin2Line />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="table-header-container">
        <h3 className="w-50 mb-0 text-primary">{"Video Tutorials List"}</h3>
        <div className="d-flex justify-content-end align-items-center gap-1">
          <div
            className="btn btn-primary btn-sm add-btn add-btn-container"
            onClick={handleAdd}
          >
            <span>Add How to Video</span>

            <span className="add-icon">
              <MdAdd />
            </span>
          </div>
        </div>
      </div>

      {renderVideoGrid()}

      <div className="how-to-videos-pagination">
        <CustomPagination
          limit={limit}
          limitHanlder={pageLimitHandler}
          pageNo={pageNo}
          pageChangeHanlder={updatePage}
          totalPage={totalPage}
          pageSizeOptions={HOW_TO_VIDEOS_PAGE_SIZE_OPTIONS}
        />
      </div>

      {/* Delete Modal */}
      <ModalV2
        isOpen={deleteModal}
        onClose={() => {
          setDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title="Delete How-to Video"
        cancelButtonLabel="No"
        confirmButtonLabel="Yes"
      >
        <div className="delete-modal-body">
          <p className="mb-2 text-muted">
            This action will permanently remove the selected video tutorial from
            the system. This cannot be undone. Please review the video details
            below before confirming.
          </p>

          {deleteTarget ? (
            <div className="delete-modal-details d-flex gap-3 align-items-start">
              <div className="delete-modal-preview" style={{ minWidth: 140 }}>
                {deleteTarget.thumbnail ? (
                  <img
                    src={deleteTarget.thumbnail}
                    alt={deleteTarget.title || "Video thumbnail"}
                    className="image-card-thumb-media"
                  />
                ) : (
                  <video
                    src={deleteTarget.videoUrl}
                    className="video-card-thumb-media"
                    muted
                    playsInline
                  />
                )}
              </div>

              <div className="delete-modal-meta">
                <div className="fw-bold" style={{ marginBottom: 6 }}>
                  {deleteTarget.title || "Untitled video"}
                </div>

                {deleteTarget.createdAt && (
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Added: {new Date(deleteTarget.createdAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-muted">No video selected.</div>
          )}
        </div>
      </ModalV2>

      <Formik
        initialValues={{
          title: selectedVideo?.title || "",
          video: null,
          existingVideoUrl: selectedVideo?.videoUrl || "",
          thumbnail: null,
          existingThumbnailUrl: selectedVideo?.thumbnail || "",
          status:
            selectedVideo?.status ?? HOW_TO_VIDEO_STATUS.PUBLISHED,
        }}
        validationSchema={
          isEditMode
            ? editHowToVideosValidationSchema
            : addHowToVideosAddValidationSchema
        }
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit: formikSubmit,
          resetForm,
          isValid,
          dirty,
          isSubmitting,
        }) => {
          const validateAndSetFile = (file) => {
            if (!file) return;

            // Check video size
            if (file.size > MAX_VIDEO_SIZE_BYTES) {
              toast.error(
                `Video size must not exceed ${MAX_VIDEO_SIZE_MB} MB.`,
              );

              // Clear the selected file
              setFieldValue("video", null);
              setFieldTouched("video", true, false);

              return;
            }

            setFieldValue("video", file);
            setFieldTouched("video", true, false);

            if (videoPreviewUrl?.startsWith("blob:")) {
              URL.revokeObjectURL(videoPreviewUrl);
            }

            const previewUrl = URL.createObjectURL(file);

            setVideoPreviewUrl(previewUrl);
          };

          const handleFileInputChange = (e) => {
            const file = e.target.files?.[0];

            validateAndSetFile(file);

            e.target.value = "";
          };

          const handleDrop = (e) => {
            e.preventDefault();

            setIsDragging(false);

            const file = e.dataTransfer.files?.[0];

            validateAndSetFile(file);
          };

          const handleDragOver = (e) => {
            e.preventDefault();

            setIsDragging(true);
          };

          const handleDragLeave = (e) => {
            e.preventDefault();

            setIsDragging(false);
          };

          const handleRemoveVideo = (e) => {
            e.stopPropagation();

            if (videoPreviewUrl?.startsWith("blob:")) {
              URL.revokeObjectURL(videoPreviewUrl);
            }

            setVideoPreviewUrl(null);

            setFieldValue("video", null);
            setFieldValue("existingVideoUrl", "");
            setFieldTouched("video", true);
          };

          const validateAndSetThumbnail = (file) => {
            if (!file) return;

            // Check image size
            if (file.size > MAX_IMAGE_SIZE_BYTES) {
              toast.error(
                `Image size must not exceed ${MAX_IMAGE_SIZE_MB} MB.`,
              );

              // Clear selected image
              setFieldValue("thumbnail", null);
              setFieldTouched("thumbnail", true, false);

              return;
            }

            setFieldValue("thumbnail", file);
            setFieldTouched("thumbnail", true, false);

            if (thumbnailPreviewUrl?.startsWith("blob:")) {
              URL.revokeObjectURL(thumbnailPreviewUrl);
            }

            const previewUrl = URL.createObjectURL(file);

            setThumbnailPreviewUrl(previewUrl);
          };

          const handleThumbnailInputChange = (e) => {
            const file = e.target.files?.[0];

            validateAndSetThumbnail(file);

            e.target.value = "";
          };

          const handleThumbnailDrop = (e) => {
            e.preventDefault();

            setIsDraggingThumbnail(false);

            const file = e.dataTransfer.files?.[0];

            validateAndSetThumbnail(file);
          };

          const handleThumbnailDragOver = (e) => {
            e.preventDefault();

            setIsDraggingThumbnail(true);
          };

          const handleThumbnailDragLeave = (e) => {
            e.preventDefault();

            setIsDraggingThumbnail(false);
          };

          const handleRemoveThumbnail = (e) => {
            e.stopPropagation();

            if (thumbnailPreviewUrl?.startsWith("blob:")) {
              URL.revokeObjectURL(thumbnailPreviewUrl);
            }

            setThumbnailPreviewUrl(null);

            setFieldValue("thumbnail", null);
            setFieldValue("existingThumbnailUrl", "");
            setFieldTouched("thumbnail", true);
          };

          const hasVideo = values.video || values.existingVideoUrl;
          const hasThumbnail = values.thumbnail || values.existingThumbnailUrl;

          return (
            <ModalV2
              isOpen={addModal}
              onClose={() => closeModal(resetForm)}
              onConfirm={formikSubmit}
              title={isEditMode ? "Edit How-to Video" : "Add How-to Video"}
              cancelButtonLabel="Back"
              confirmButtonLabel={
                isSubmitting
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                    ? "Update"
                    : "Save"
              }
              confirmDisabled={isSubmitting}
            >
              <Form>
                <div className="form-group-v2">
                  <label htmlFor="title">
                    Tutorial Title
                    <span className="required-star">*</span>
                  </label>

                  <input
                    id="title"
                    name="title"
                    type="text"
                    className="form-input-v2"
                    placeholder="e.g. Enter your tutorial title"
                    value={values.title}
                    onChange={(e) => {
                      handleChange(e);
                      // Show max-length error while typing, not only on submit
                      if (e.target.value.length > 120) {
                        setFieldTouched("title", true, false);
                      }
                    }}
                    onBlur={handleBlur}
                  />

                  {touched.title && errors.title && (
                    <div className="form-error-v2">{errors.title}</div>
                  )}
                </div>

                <div className="form-group-v2">
                  <StatusSelect
                    label="Tutorial Status"
                    required
                    value={values.status}
                    options={HOW_TO_VIDEO_STATUS_OPTIONS}
                    onChange={(status) => {
                      setFieldValue("status", status);
                      setFieldTouched("status", true, false);
                    }}
                    onBlur={() => setFieldTouched("status", true)}
                    error={
                      touched.status && errors.status ? errors.status : ""
                    }
                  />
                </div>

                <div className="form-group-v2">
                  <label htmlFor="tutorialThumbnail">
                    Thumbnail Image
                    <span className="required-star">*</span>
                  </label>

                  {!hasThumbnail ? (
                    <div
                      className={`video-dropzone thumbnail-dropzone ${
                        isDraggingThumbnail ? "dragging" : ""
                      }`}
                      onClick={() => thumbnailInputRef.current?.click()}
                      onDrop={handleThumbnailDrop}
                      onDragOver={handleThumbnailDragOver}
                      onDragLeave={handleThumbnailDragLeave}
                    >
                      <div className="video-dropzone-icon">
                        <MdCloudUpload />
                      </div>

                      <div className="video-dropzone-title">
                        Drag & drop a thumbnail here
                      </div>

                      <div className="video-dropzone-sub">
                        or <span className="video-dropzone-browse">browse</span>{" "}
                        from your computer &middot; JPG, PNG, JPEG up to{" "}
                        {MAX_IMAGE_SIZE_MB}MB
                      </div>

                      <input
                        ref={thumbnailInputRef}
                        id="tutorialThumbnail"
                        name="thumbnail"
                        type="file"
                        accept={AllowedImageTypes}
                        onChange={handleThumbnailInputChange}
                        onBlur={() => setFieldTouched("thumbnail", true)}
                      />
                    </div>
                  ) : (
                    <div className="video-preview-card thumbnail-preview-card">
                      <img
                        className="thumbnail-preview-image"
                        src={thumbnailPreviewUrl}
                        alt="Thumbnail preview"
                      />

                      <div className="video-preview-meta">
                        <div className="video-preview-info">
                          <div className="video-preview-file-icon">
                            <FaFileImage />
                          </div>

                          <div className="video-preview-text">
                            <div className="video-preview-name">
                              {values.thumbnail
                                ? values.thumbnail.name
                                : "Existing thumbnail image"}
                            </div>

                            {values.thumbnail && (
                              <div className="video-preview-size">
                                {formatFileSize(values.thumbnail.size)}
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="video-preview-remove"
                          onClick={handleRemoveThumbnail}
                        >
                          <RxCross2 />
                        </button>
                      </div>
                    </div>
                  )}

                  {touched.thumbnail && errors.thumbnail && (
                    <div className="form-error-v2">{errors.thumbnail}</div>
                  )}
                </div>

                <div className="form-group-v2">
                  <label htmlFor="tutorialVideo">
                    Tutorial Video
                    <span className="required-star">*</span>
                  </label>

                  {!hasVideo ? (
                    <div
                      className={`video-dropzone ${
                        isDragging ? "dragging" : ""
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      <div className="video-dropzone-icon">
                        <MdCloudUpload />
                      </div>

                      <div className="video-dropzone-title">
                        Drag & drop your video here
                      </div>

                      <div className="video-dropzone-sub">
                        or <span className="video-dropzone-browse">browse</span>{" "}
                        from your computer &middot; MP4 up to{" "}
                        {MAX_VIDEO_SIZE_MB}MB
                      </div>

                      <input
                        ref={fileInputRef}
                        id="tutorialVideo"
                        name="video"
                        type="file"
                        accept={AllowedVideoTypes || ACCEPTED_TYPES.join(",")}
                        onChange={handleFileInputChange}
                        onBlur={() => setFieldTouched("video", true)}
                      />
                    </div>
                  ) : (
                    <div className="video-preview-card">
                      <video
                        className="video-preview-player"
                        src={videoPreviewUrl}
                        controls
                      />

                      <div className="video-preview-meta">
                        <div className="video-preview-info">
                          <div className="video-preview-file-icon">
                            <FaFileVideo />
                          </div>

                          <div className="video-preview-text">
                            <div className="video-preview-name">
                              {values.video
                                ? values.video.name
                                : "Existing tutorial video"}
                            </div>

                            {values.video && (
                              <div className="video-preview-size">
                                {formatFileSize(values.video.size)}
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="video-preview-remove"
                          onClick={handleRemoveVideo}
                        >
                          <RxCross2 />
                        </button>
                      </div>
                    </div>
                  )}

                  {touched.video && errors.video && (
                    <div className="form-error-v2">{errors.video}</div>
                  )}
                </div>
              </Form>
            </ModalV2>
          );
        }}
      </Formik>
    </div>
  );
};

export default HowToVideos;
